import { useMutation } from '@apollo/client'
import {
  createReportWithVictim,
  createReportWithVictimKey,
  encryptFileWithReportKey,
  serializePin,
} from '@faceup/crypto'
import { useLocalization } from '@faceup/localization'
import { CustomFormItems } from '@faceup/report'
import {
  Form,
  followUpVariant,
  Prompt,
  ScrollableCheckboxList,
  type ScrollableCheckboxListOption,
} from '@faceup/ui'
import {
  Button,
  Checkbox,
  Input,
  Select,
  Skeleton,
  Typography,
  useNotification,
} from '@faceup/ui-base'
import {
  FormItemType,
  type Language,
  Plan,
  RecipientsOnReportForm,
  getTranslation as utilsGetTranslation,
} from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { useId } from '@mantine/hooks'
import { useContext, useEffect, useMemo, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { TextAreaWithVoiceRecordings } from '../../Components/VoiceRecording/TextAreaWithVoiceRecordings'
import { VoiceRecordingModal } from '../../Components/VoiceRecording/VoiceRecordingModal'
import { UserContext } from '../../Contexts/UserContext'
import type { CustomValues } from '../../hooks/useCustomValues'
import { sharedMessages } from '../../Shared/translations'
import { defineMessages, FormattedMessage, useIntl } from '../../TypedIntl'
import useAnalytics from '../../utils/analytics'
import { isFromAkutan } from '../../utils/urls'
import useReportAuth from '../../utils/useReportAuth'
import { AttachmentsDropzoneFormItem } from './AttachmentsDropzoneFormItem'
import { RecipientPopoverContent } from './components/RecipientPopoverContent'
import { useManageCreateReportForm } from './hooks/useManageCreateReportForm'
import type { Recipient } from './hooks/useRecipients'
import { useVoiceRecordings } from './hooks/useVoiceRecordings'

const { Text } = Typography

const readPxVar = (name: string) =>
  Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0

const scrollToFirstErroredField = () => {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>('[aria-invalid="true"]')
    if (!el) {
      return
    }
    const offset = readPxVar('--followup-header-height') + 12 + readPxVar('--safe-area-inset-top')
    const top = el.getBoundingClientRect().top + window.scrollY - offset
    window.scrollTo({ top, behavior: 'smooth' })
    el.focus({ preventScroll: true })
  })
}

const messages = defineMessages({
  navigationPrompt: 'FollowUp.CreateReportForm.navigationPrompt',
  institutionLabel: 'Shared.report.organization',
  moreInfoLabel: 'Shared.report.moreInfo',
  recipientsLabel: 'FollowUp.report.recipients',
  recipientsDetail: 'FollowUp.report.recipientsDetail',
  recipientsDetailEditable: 'FollowUp.report.recipientsDetailEditable',
  moreInfoError: 'FollowUp.CreateReportForm.moreInfoError',
  moreInfoDetailSchool: 'Shared.CreateReportForm.school.moreInfoDetail',
  moreInfoDetailCompany: 'Shared.CreateReportForm.company.moreInfoDetail',
  classRoomLabel: 'Shared.report.classRoom',
  victimNameLabel: 'Shared.report.victimName',
  victimNameError: 'Shared.global.invalidInput.name',
  categoryLabel: 'Shared.report.category',
  recipientsCantBeShown: 'FollowUp.report.recipientsCantBeShown',
  recipientsSelectCategoryAndOrganisationUnitFirst:
    'FollowUp.report.recipientsSelectCategoryAndOrganisationUnitFirst',
  recipientsSelectOrganizationUnitFirst: 'FollowUp.report.recipientsSelectOrganisationUnitFirst',
  voiceRecordingLabel: 'FollowUp.CreateReportForm.voiceRecordingLabel',
  voiceRecordingButtonLabel: 'FollowUp.CreateReportForm.voiceRecordingButtonLabel',
  voiceRecordingButtonMaxReachedLabel:
    'FollowUp.CreateReportForm.voiceRecordingButtonMaxReachedLabel',
  senderIdentificationLabel: 'FollowUp.CreateReportForm.senderIdentificationLabel',
  sendAnonymously: 'FollowUp.CreateReportForm.sendAnonymously',
  anonymousSelected: 'FollowUp.CreateReportForm.a11y.anonymousSelected',
  nonAnonymousSelected: 'FollowUp.CreateReportForm.a11y.nonAnonymousSelected',
  submitErrorSummary: 'FollowUp.CreateReportForm.submitErrorSummary',
})

const fragments = {
  CreateReportForm_reportSource: graphql(`
    fragment CreateReportForm_reportSource on PublicReportSource {
      id
      isDefault
      ...useManageCreateReportForm_publicReportSource

      categories {
        id
        name
      }
      defaultLanguage
      identityExcludedOrganizationalUnitPublicIds
      isAnonymityEnabled
      config {
        ... on FormReportSourceConfiguration {
          id
          recipientsOnReportForm
          attachmentsEnabled
          voiceSubmissionEnabled
          isAnonymityPreselected
          formItems {
            id
            type
            labelTranslations {
              language
              translation
            }
            hasHint
            hintTranslations {
              language
              translation
            }
          }
          identificationFormItems {
            id
            type
            labelTranslations {
              language
              translation
            }
            hasHint
            hintTranslations {
              language
              translation
            }
          }
        }
      }

      organizationalUnits {
        id
        organizationalUnitName
      }
    }
  `),
  CreateReportFormInstitution: graphql(`
    fragment CreateReportForm_institution on PublicCompany {
      id
      organizationalUnitName
      isE2EE
      type

      billing {
        id
        plan
      }

      partner {
        id
        name
      }

      config {
        id
        institutionName
      }

      ...useManageCreateReportForm_publicCompany
      ...AttachmentsDropzoneFormItem_institution
    }
  `),
}

type CreateReportFormProps = {
  loading?: boolean
  institution: FragmentType<typeof fragments.CreateReportFormInstitution>
  reportSource: FragmentType<typeof fragments.CreateReportForm_reportSource>
  onSent: (pin: string | null, tag: string) => void
  publicKey: string
  customValues?: CustomValues
}

const mutations = {
  CreateReport: graphql(`
    mutation CreateReportMutation($input: CreateReportInput!) {
      createReport(input: $input) {
        victimIdentity
        createdCase {
          id
          tag
        }
      }
    }
  `),
}

export const CreateReportForm = ({
  onSent,
  loading = false,
  institution: _institution,
  reportSource: _reportSource,
  customValues,
  publicKey,
}: CreateReportFormProps) => {
  const institution = getFragmentData(fragments.CreateReportFormInstitution, _institution)
  const reportSource = getFragmentData(fragments.CreateReportForm_reportSource, _reportSource)
  const { language } = useLocalization()
  const [loadingReport, setLoadingReport] = useState(false)
  const [hasSubmitAttemptError, setHasSubmitAttemptError] = useState(false)
  const ariaPrefix = useId()
  const { formatMessage } = useIntl()
  const { reportSource: institutionFromContext } = useContext(UserContext)
  const { track } = useAnalytics()
  const notification = useNotification()

  const { logout } = useReportAuth()

  const [createReport, { called }] = useMutation(mutations.CreateReport, {
    onError: e => {
      // inspired by logout function in apps/follow-up/src/Components/LogoutButton/LogoutButton.tsx
      if (e.message === 'Wrong company password') {
        window.location.href = '/'
      }
      setLoadingReport(false)
    },
  })

  const formConfig =
    reportSource.config?.__typename === 'FormReportSourceConfiguration' ? reportSource.config : null

  const {
    categoryInput,
    setCategoryInput,
    moreInfoInput,
    setMoreInfoInput,
    senderNameInput,
    setSenderNameInput,
    nameInput,
    setNameInput,
    attachments,
    setAttachments,
    customFormItems,
    setCustomFormItems,
    getValuesForMutation,
    clearForm,
    validateForm,
    organizationUnitInput,
    setOrganizationUnitInput,
    selectableOrganizationUnits,
    isAnonymous,
    setIsAnonymous,
    attachmentsEnabled,
    setAttachmentsEnabled,
    recipientsOnReportForm,
    setRecipientsOnReportForm,
    recipients,
    areRecipientsLoading,
    changeRecipient,
    isSchoolDefaultForm,
  } = useManageCreateReportForm({
    institution: institution ?? null,
    reportSource: reportSource ?? null,
  })

  const voiceRecordings = useVoiceRecordings()

  useEffect(() => {
    const category = reportSource.categories[0]
    if (category && isFromAkutan(document.referrer)) {
      setCategoryInput({
        value: category.id,
        error: false,
      })
    }
  }, [reportSource.categories, setCategoryInput])

  const isAnonymityEnabled =
    (customValues?.anonymityEnabled ?? reportSource.isAnonymityEnabled ?? true) &&
    !reportSource.identityExcludedOrganizationalUnitPublicIds.includes(
      organizationUnitInput.value ?? ''
    )

  const isAnonymityPreselected =
    customValues?.anonymityPreselected ?? formConfig?.isAnonymityPreselected ?? true

  useEffect(() => {
    if (customValues?.categoryId) {
      setCategoryInput({ value: customValues?.categoryId, error: false })
    }
  }, [customValues?.categoryId, setCategoryInput])

  const getTranslation = <D extends Language>(
    translations: { language: D; translation: string }[] | undefined
  ): string => utilsGetTranslation(translations ?? [], language, reportSource.defaultLanguage)

  const formItemCategories = formConfig?.formItems?.find(
    formItem => formItem.type === FormItemType.Category
  )
  const formItemOrganisationUnit = formConfig?.formItems?.find(
    formItem => formItem.type === FormItemType.OrganizationalUnit
  )
  const formItemMoreInformation = formConfig?.formItems?.find(
    formItem => formItem.type === FormItemType.MoreInformation
  )
  const formItemSenderName = formConfig?.identificationFormItems?.find(
    formItem => formItem.type === FormItemType.SenderName
  )
  const formItemClassroom = formConfig?.formItems?.find(
    formItem => formItem.type === FormItemType.Classroom
  )
  const categoriesLabel: string =
    getTranslation(formItemCategories?.labelTranslations) || formatMessage(messages.categoryLabel)
  const categoriesHint: string | undefined =
    formItemCategories?.hasHint && formItemCategories?.hintTranslations
      ? getTranslation(formItemCategories?.hintTranslations)
      : undefined
  const organisationUnitsLabel: string =
    getTranslation(formItemOrganisationUnit?.labelTranslations) ||
    formatMessage(messages.institutionLabel)
  const organisationUnitsHint: string | undefined =
    formItemOrganisationUnit?.hasHint && formItemOrganisationUnit?.hintTranslations
      ? getTranslation(formItemOrganisationUnit?.hintTranslations)
      : undefined
  const moreInformationLabel: string =
    getTranslation(formItemMoreInformation?.labelTranslations) ||
    formatMessage(messages.moreInfoLabel)
  const moreInformationHint: string | undefined = formItemMoreInformation?.hasHint
    ? (formItemMoreInformation?.hintTranslations &&
        getTranslation(formItemMoreInformation?.hintTranslations)) ||
      formatMessage(
        !isSchoolDefaultForm ? messages.moreInfoDetailCompany : messages.moreInfoDetailSchool
      )
    : undefined
  const senderNameLabel: string =
    getTranslation(formItemSenderName?.labelTranslations) ||
    formatMessage(sharedMessages.globalNameLabel)
  const senderNameHint: string | undefined =
    formItemSenderName?.hasHint && formItemSenderName?.hintTranslations
      ? getTranslation(formItemSenderName?.hintTranslations)
      : undefined
  const classroomLabel: string =
    getTranslation(formItemClassroom?.labelTranslations) || formatMessage(messages.classRoomLabel)
  const classroomHint: string | undefined =
    formItemClassroom?.hasHint && formItemClassroom?.hintTranslations
      ? getTranslation(formItemClassroom?.hintTranslations)
      : undefined

  const canUseVoiceRecordButton = useMemo(() => {
    const isVoiceSubmissionAllowed =
      !isSchoolDefaultForm || institution?.billing?.plan === Plan.Standard
    const isVoiceSubmissionEnabled =
      customValues?.voiceSubmissionEnabled ?? formConfig?.voiceSubmissionEnabled ?? true
    return isVoiceSubmissionAllowed && isVoiceSubmissionEnabled
  }, [
    institution?.billing?.plan,
    isSchoolDefaultForm,
    customValues?.voiceSubmissionEnabled,
    formConfig?.voiceSubmissionEnabled,
  ])

  useEffect(() => {
    setIsAnonymous(isAnonymityEnabled && isAnonymityPreselected)
  }, [isAnonymityPreselected, setIsAnonymous, isAnonymityEnabled])

  useEffect(() => {
    setAttachmentsEnabled({
      value: customValues?.attachmentsEnabled ?? formConfig?.attachmentsEnabled ?? true,
      error: false,
    })
  }, [customValues?.attachmentsEnabled, formConfig?.attachmentsEnabled, setAttachmentsEnabled])

  useEffect(() => {
    setRecipientsOnReportForm({
      value:
        customValues?.recipientsOnReportForm ??
        formConfig?.recipientsOnReportForm ??
        RecipientsOnReportForm.Visible,
      error: false,
    })
  }, [
    customValues?.recipientsOnReportForm,
    formConfig?.recipientsOnReportForm,
    setRecipientsOnReportForm,
  ])

  useEffect(() => {
    if (reportSource.categories.length === 1 && !categoryInput.value) {
      setCategoryInput({ value: reportSource.categories[0]?.id ?? '', error: false })
    }
  }, [reportSource, categoryInput, setCategoryInput])

  const isCheckPromptNeeded = useMemo(
    () =>
      (!categoryInput.value ||
        senderNameInput.value.trim().length > 2 ||
        moreInfoInput.value.trim().length > 2 ||
        attachments.length > 0) &&
      !called,
    [attachments.length, called, categoryInput.value, moreInfoInput.value, senderNameInput.value]
  )

  const categories = reportSource.categories ?? []
  const shouldShowCategorySelect = categories.length > 1

  const categoryResolved = Boolean(categoryInput.value)
  const orgUnitResolved =
    selectableOrganizationUnits.length <= 1 || Boolean(organizationUnitInput.value)
  const showFormBody = categoryResolved && orgUnitResolved

  return (
    <Form
      variant={followUpVariant}
      onSubmit={async () => {
        setLoadingReport(true)
        const { isFormValid } = await validateForm({
          hasVoiceRecordings: voiceRecordings.recordings.length > 0,
        })
        if (!isFormValid) {
          setLoadingReport(false)
          setHasSubmitAttemptError(true)
          scrollToFirstErroredField()
          return
        }
        setHasSubmitAttemptError(false)

        const reportKey = await createReportWithVictimKey()

        const payload = await createReportWithVictim({
          body: {
            moreInfo: moreInfoInput.value,
            victimName: nameInput.value.trim() || senderNameInput.value.trim(),
            attachments: attachments.map(({ file }) => file),
          },
          // Only selected recipients with a valid public key can be encrypted to;
          // keyless selections would fail at crypto_box_seal.
          recipients: recipients.flatMap(member =>
            member.isSelected && member.publicKey
              ? [{ id: member.id ?? '', key: member.publicKey }]
              : []
          ),
          systemPbk: institution?.isE2EE ? undefined : publicKey,
          reportKey,
        })

        // this is necessary to explicitly distinguish from other cases
        // where the user shouldn't have access
        // (wrong OU, category)
        const recipientsToRestrict = recipients
          .filter(recipient => !recipient.isSelected)
          .map(recipient => ({
            id: recipient.id,
            key: '',
            restricted: true,
          }))

        if (payload.isErr()) {
          setLoadingReport(false)
          console.error(payload.error.message)
          notification.error({
            message: formatMessage(sharedMessages.encryptionError),
            description: payload.error.message,
          })
          return
        }

        const {
          victimPin,
          privateKeyEncrypted,
          passwordPrehash,
          passwordKey: _passwordKey,
          recipientKeys,
          ...rest
        } = payload.value

        const encryptedVoiceFiles = await Promise.all(
          voiceRecordings.files.map(async file => {
            const result = await encryptFileWithReportKey(file, reportKey)
            if (result.isErr()) {
              throw new Error(`Failed to encrypt voice recording: ${result.error.message}`)
            }
            return result.value
          })
        )

        const response = await createReport({
          variables: {
            input: {
              reportSourceId: institutionFromContext?.defaultFormId ?? '',
              companyId: organizationUnitInput.value ?? '',
              password: institutionFromContext?.password,
              categoryId: categoryInput.value ?? '',
              privateKeyEncrypted,
              passwordPrehash,
              answers: await getValuesForMutation(reportKey),
              recipientKeys: [...recipientKeys, ...recipientsToRestrict],
              moreInfoAttachments: encryptedVoiceFiles,
              ...rest,
            },
          },
        })
        if (response?.errors) {
          setLoadingReport(false)
          return
        }

        if (!response) {
          setLoadingReport(false)
          console.error(response)
          notification.error({
            message: formatMessage(sharedMessages.encryptionError),
          })
          return
        }

        const identity = response?.data?.createReport?.victimIdentity ?? ''
        const pin = serializePin(identity + victimPin)

        await logout()
        clearForm()
        voiceRecordings.reset()
        track(
          'Report created',
          isSchoolDefaultForm
            ? { isSchoolDefaultForm: true }
            : {
                isSchoolDefaultForm: false,
                isAnonymous,
                wasAnonymityPossible: isAnonymityEnabled,
                wasAnonymityPreselected: isAnonymityPreselected,
              }
        )
        onSent(pin, response?.data?.createReport?.createdCase?.tag ?? '')
        setLoadingReport(false)
      }}
    >
      <Prompt when={isCheckPromptNeeded} message={formatMessage(messages.navigationPrompt)} />
      {shouldShowCategorySelect && (
        <Form.Item
          label={categoriesLabel}
          hint={categoriesHint}
          ariaIds={{
            hintId: categoriesHint ? `${ariaPrefix}-category-hint` : undefined,
            errorId: categoryInput.error ? `${ariaPrefix}-category-error` : undefined,
          }}
          errorMessage={
            categoryInput.error && <FormattedMessage {...sharedMessages.fieldRequired} />
          }
          withAsterisk
          formItemProps={{
            'data-test': 'create-company-report-form-category',
          }}
        >
          <Select
            placeholder={formatMessage(sharedMessages.selectPlaceholder)}
            loading={loading}
            value={categoryInput.value}
            onChange={value => {
              setCategoryInput({ value, error: false })
              setCustomFormItems({})
            }}
            allowClear
            aria-describedby={categoriesHint ? `${ariaPrefix}-category-hint` : undefined}
            aria-errormessage={categoryInput.error ? `${ariaPrefix}-category-error` : undefined}
            aria-invalid={Boolean(categoryInput.error)}
            data-test='create-company-report-form-category-input'
            popupRender={menu => (
              <div data-test='create-company-report-form-category-menu'>{menu}</div>
            )}
            style={{ width: '100%' }}
            options={categories.map(category => ({
              value: category.id,
              label: category.name,
              'data-test': 'create-company-report-form-category-item',
            }))}
          />
        </Form.Item>
      )}
      {selectableOrganizationUnits.length > 1 && (
        <Form.Item
          label={organisationUnitsLabel}
          hint={organisationUnitsHint}
          ariaIds={{
            hintId: organisationUnitsHint ? `${ariaPrefix}-org-unit-hint` : undefined,
            errorId: organizationUnitInput.error ? `${ariaPrefix}-org-unit-error` : undefined,
          }}
          errorMessage={
            organizationUnitInput.error && <FormattedMessage {...sharedMessages.fieldRequired} />
          }
          withAsterisk
          formItemProps={{
            'data-test': 'create-company-report-form-company',
          }}
        >
          <Select
            placeholder={formatMessage(sharedMessages.selectPlaceholder)}
            loading={loading}
            onChange={value => setOrganizationUnitInput({ value, error: false })}
            allowClear
            aria-describedby={organisationUnitsHint ? `${ariaPrefix}-org-unit-hint` : undefined}
            aria-errormessage={
              organizationUnitInput.error ? `${ariaPrefix}-org-unit-error` : undefined
            }
            aria-invalid={Boolean(organizationUnitInput.error)}
            data-test='create-company-report-form-company-input'
            popupRender={menu => (
              <div data-test='create-company-report-form-company-menu'>{menu}</div>
            )}
            style={{ width: '100%' }}
            options={selectableOrganizationUnits.map(i => ({
              value: i.id,
              label: i.organizationalUnitName,
            }))}
          />
        </Form.Item>
      )}
      {showFormBody && (
        <>
          {isSchoolDefaultForm && (
            <>
              <Form.Item
                label={<FormattedMessage {...messages.victimNameLabel} />}
                errorMessage={nameInput.error && <FormattedMessage {...messages.victimNameError} />}
                withAsterisk
                formItemProps={{
                  'data-test': 'create-school-report-form-victim-name',
                }}
              >
                <Input
                  autoComplete='off'
                  onChange={({ target: { value } }) => {
                    setNameInput({ value, error: false })
                  }}
                  value={nameInput.value}
                  data-test='create-school-report-form-victim-name-input'
                />
              </Form.Item>
              {formItemClassroom && (
                <Form.Item
                  label={classroomLabel}
                  hint={classroomHint}
                  ariaIds={{
                    hintId: classroomHint ? `${ariaPrefix}-classroom-hint` : undefined,
                    errorId: customFormItems[formItemClassroom.id]?.error
                      ? `${ariaPrefix}-classroom-error`
                      : undefined,
                  }}
                  errorMessage={
                    customFormItems[formItemClassroom.id]?.error && (
                      <FormattedMessage {...sharedMessages.fieldRequired} />
                    )
                  }
                  withAsterisk
                  formItemProps={{
                    'data-test': 'create-company-report-form-class-room',
                  }}
                >
                  <Input
                    autoComplete='off'
                    aria-describedby={classroomHint ? `${ariaPrefix}-classroom-hint` : undefined}
                    aria-errormessage={
                      customFormItems[formItemClassroom.id]?.error
                        ? `${ariaPrefix}-classroom-error`
                        : undefined
                    }
                    aria-invalid={Boolean(customFormItems[formItemClassroom.id]?.error)}
                    value={(customFormItems[formItemClassroom.id]?.values?.[0] as string) ?? ''}
                    onChange={({ target: { value } }) => {
                      setCustomFormItems({
                        ...customFormItems,
                        [formItemClassroom.id]: { type: 'text', values: [value] },
                      })
                    }}
                    data-test='create-company-report-form-class-room-input'
                  />
                </Form.Item>
              )}
            </>
          )}

          {recipientsOnReportForm.value !== RecipientsOnReportForm.Invisible && (
            <Form.Item
              withAsterisk
              label={<FormattedMessage {...messages.recipientsLabel} />}
              hint={formatMessage(
                recipientsOnReportForm.value === RecipientsOnReportForm.Editable
                  ? messages.recipientsDetailEditable
                  : messages.recipientsDetail
              )}
              ariaIds={{ hintId: `${ariaPrefix}-recipients-hint` }}
              formItemProps={{
                'data-test': 'create-company-report-recipients',
              }}
            >
              <Recipients
                selectedOrganizationUnit={organizationUnitInput.value}
                selectedCategory={categoryInput.value}
                areRecipientsLoading={areRecipientsLoading}
                recipients={recipients}
                changeRecipient={changeRecipient}
                institution={institution}
              />
            </Form.Item>
          )}
          <CustomFormItems
            categoryId={categoryInput.value ?? ''}
            reportSourceId={institutionFromContext?.defaultFormId ?? ''}
            values={customFormItems}
            onChange={(key, answer) => {
              setCustomFormItems({ ...customFormItems, [key]: { ...answer } })
            }}
            overrideFormData={customValues}
            moreInformationRender={
              <Form.Item
                key='moreInformation'
                withAsterisk={!isSchoolDefaultForm}
                label={moreInformationLabel}
                hint={moreInformationHint}
                ariaIds={{
                  hintId: moreInformationHint ? `${ariaPrefix}-more-info-hint` : undefined,
                  errorId: moreInfoInput.error ? `${ariaPrefix}-more-info-error` : undefined,
                }}
                errorMessage={
                  moreInfoInput.error && <FormattedMessage {...messages.moreInfoError} />
                }
                formItemProps={{
                  'data-test': 'create-company-report-form-more-info',
                }}
              >
                <TextAreaWithVoiceRecordings
                  style={{ minHeight: 100, paddingBottom: '2rem' }}
                  rows={4}
                  aria-describedby={
                    moreInformationHint ? `${ariaPrefix}-more-info-hint` : undefined
                  }
                  aria-errormessage={
                    moreInfoInput.error ? `${ariaPrefix}-more-info-error` : undefined
                  }
                  aria-invalid={Boolean(moreInfoInput.error)}
                  value={moreInfoInput.value}
                  onChange={({ target: { value } }) => {
                    setMoreInfoInput({ value, error: false })
                  }}
                  data-test='create-company-report-form-more-info-input'
                  voiceSubmissionEnabled={canUseVoiceRecordButton}
                  voiceRecordings={voiceRecordings.recordings}
                  onVoiceRecordingButtonClick={voiceRecordings.openModal}
                  onRemoveRecording={voiceRecordings.handleRemove}
                  recordingLabel={n => formatMessage(messages.voiceRecordingLabel, { number: n })}
                  voiceRecordingButtonLabel={formatMessage(messages.voiceRecordingButtonLabel)}
                  voiceRecordingButtonMaxReachedLabel={formatMessage(
                    messages.voiceRecordingButtonMaxReachedLabel
                  )}
                />
              </Form.Item>
            }
          />

          {attachmentsEnabled.value && (
            <AttachmentsDropzoneFormItem
              institution={institution}
              attachments={attachments}
              onSetAttachments={setAttachments}
            />
          )}

          {!isSchoolDefaultForm && (
            <fieldset
              className='rounded-[20px] bg-colorFillTertiary px-6 pb-8 pt-8 flex flex-col gap-4 border-none'
              aria-label={formatMessage(messages.senderIdentificationLabel)}
            >
              {isAnonymityEnabled && (
                <Form.Item>
                  <Checkbox
                    onChange={e => {
                      setIsAnonymous(e.target.checked)
                      setSenderNameInput({ value: '', error: false })
                    }}
                    checked={isAnonymous}
                    data-test='create-company-report-form-anonymously-checkbox'
                  >
                    <FormattedMessage
                      {...messages.sendAnonymously}
                      values={{ bold: chunks => <strong key='bold'>{chunks}</strong> }}
                    />
                  </Checkbox>
                </Form.Item>
              )}

              {!isAnonymous && (
                <Form.Item
                  label={senderNameLabel}
                  hint={senderNameHint}
                  ariaIds={{
                    hintId: senderNameHint ? `${ariaPrefix}-sender-name-hint` : undefined,
                    errorId: senderNameInput.error ? `${ariaPrefix}-sender-name-error` : undefined,
                  }}
                  errorMessage={
                    senderNameInput.error && <FormattedMessage {...sharedMessages.fieldRequired} />
                  }
                  withAsterisk
                  formItemProps={{
                    'data-test': 'create-company-report-form-victim-name',
                  }}
                >
                  <Input
                    autoComplete='name'
                    aria-describedby={senderNameHint ? `${ariaPrefix}-sender-name-hint` : undefined}
                    aria-errormessage={
                      senderNameInput.error ? `${ariaPrefix}-sender-name-error` : undefined
                    }
                    aria-invalid={Boolean(senderNameInput.error)}
                    onChange={({ target: { value } }) => {
                      setSenderNameInput({ value, error: false })
                    }}
                    data-test='create-company-report-form-victim-name-input'
                  />
                </Form.Item>
              )}

              {isAnonymityEnabled && (
                <span className='sr-only' aria-live='polite' aria-atomic='true'>
                  {isAnonymous
                    ? formatMessage(messages.anonymousSelected)
                    : formatMessage(messages.nonAnonymousSelected)}
                </span>
              )}

              {hasSubmitAttemptError && (
                <output className='text-colorError text-center'>
                  <FormattedMessage {...messages.submitErrorSummary} />
                </output>
              )}
              <Button
                type='primary'
                htmlType='submit'
                loading={loadingReport || loading}
                block
                data-test='create-company-report-form-submit'
              >
                <FormattedMessage {...sharedMessages.submit} />
              </Button>
            </fieldset>
          )}

          {isSchoolDefaultForm && (
            <>
              {hasSubmitAttemptError && (
                <output className='text-colorError text-center'>
                  <FormattedMessage {...messages.submitErrorSummary} />
                </output>
              )}
              <Button
                type='primary'
                htmlType='submit'
                loading={loadingReport || loading}
                block
                data-test='create-company-report-form-submit'
              >
                <FormattedMessage {...sharedMessages.submit} />
              </Button>
            </>
          )}
        </>
      )}
      <VoiceRecordingModal
        isVisible={voiceRecordings.isModalOpen}
        onClose={voiceRecordings.closeModal}
        onRecordingSave={voiceRecordings.handleRecordingSaved}
      />
    </Form>
  )
}

const Recipients = ({
  selectedOrganizationUnit,
  selectedCategory,
  recipients,
  changeRecipient,
  areRecipientsLoading,
  institution,
}: {
  selectedOrganizationUnit?: string
  selectedCategory?: string
  recipients: Recipient[]
  changeRecipient: (member: Recipient) => void
  areRecipientsLoading: boolean
  institution: ResultOf<typeof fragments.CreateReportFormInstitution>
}) => {
  const { formatMessage } = useIntl()

  const partnerName = institution?.partner?.name ?? null
  const institutionName = institution?.config?.institutionName ?? null

  const options = useMemo<ScrollableCheckboxListOption[]>(
    () =>
      recipients.map(recipient => ({
        id: recipient.id,
        label: recipient.name,
        disabled: !recipient.isEditable,
        popoverContent: (
          <RecipientPopoverContent
            recipient={recipient}
            companyName={recipient.isPartner ? partnerName : institutionName}
          />
        ),
      })),
    [recipients, partnerName, institutionName]
  )

  const selectedIds = useMemo(
    () => recipients.filter(r => r.isSelected).map(r => r.id),
    [recipients]
  )

  if (!selectedOrganizationUnit || !selectedCategory) {
    if (selectedOrganizationUnit) {
      return (
        <Text type='secondary'>
          <FormattedMessage {...messages.recipientsCantBeShown} />
        </Text>
      )
    }
    if (selectedCategory) {
      return (
        <Text type='secondary'>
          <FormattedMessage {...messages.recipientsSelectOrganizationUnitFirst} />
        </Text>
      )
    }
    return (
      <Text type='secondary'>
        <FormattedMessage {...messages.recipientsSelectCategoryAndOrganisationUnitFirst} />
      </Text>
    )
  }

  if (areRecipientsLoading) {
    return <Skeleton.Avatar />
  }

  return (
    <ScrollableCheckboxList
      aria-label={formatMessage(messages.recipientsLabel)}
      options={options}
      selectedIds={selectedIds}
      onChange={id => {
        const recipient = recipients.find(r => r.id === id)
        if (recipient) {
          changeRecipient(recipient)
        }
      }}
    />
  )
}
